/**
 * The realm map's camera: where the painting sits on screen and how far in
 * it is zoomed. `zoom` is screen pixels per master pixel; `x`/`y` are the
 * screen position of the painting's top-left corner. Pure functions, so the
 * clamping is tested without a browser.
 */

export interface Camera {
  x: number;
  y: number;
  zoom: number;
}

export interface Size {
  w: number;
  h: number;
}

/** Closest in: one master pixel per screen pixel. */
export const MAX_ZOOM = 1;

/** Closest out: the painting just covers the screen (there's no art past
 *  its edges). */
export function minZoom(map: Size, view: Size): number {
  return Math.max(view.w / map.w, view.h / map.h);
}

/** Keep the painting covering the screen at a zoom it allows. */
export function clampCamera(cam: Camera, map: Size, view: Size): Camera {
  const zoom = Math.min(MAX_ZOOM, Math.max(minZoom(map, view), cam.zoom));
  const w = map.w * zoom;
  const h = map.h * zoom;
  const x = w <= view.w ? (view.w - w) / 2 : Math.min(0, Math.max(view.w - w, cam.x));
  const y = h <= view.h ? (view.h - h) / 2 : Math.min(0, Math.max(view.h - h, cam.y));
  return { x, y, zoom };
}

/** Zoom by `factor` about a screen point, which stays over the same spot
 *  of the painting (as far as the clamp allows). */
export function zoomAbout(
  cam: Camera,
  px: number,
  py: number,
  factor: number,
  map: Size,
  view: Size,
): Camera {
  const mx = (px - cam.x) / cam.zoom;
  const my = (py - cam.y) / cam.zoom;
  const zoom = Math.min(MAX_ZOOM, Math.max(minZoom(map, view), cam.zoom * factor));
  return clampCamera({ x: px - mx * zoom, y: py - my * zoom, zoom }, map, view);
}

/** Centre the screen on a point of the painting (master pixels). */
export function centreOn(mx: number, my: number, zoom: number, map: Size, view: Size): Camera {
  const z = Math.min(MAX_ZOOM, Math.max(minZoom(map, view), zoom));
  return clampCamera({ x: view.w / 2 - mx * z, y: view.h / 2 - my * z, zoom: z }, map, view);
}

/**
 * The opening zoom. A landscape screen sees the whole realm across its
 * width; a portrait one (a phone) sees about one world, `worldWidth`
 * master pixels across.
 */
export function openingZoom(map: Size, view: Size, worldWidth = 1000): number {
  if (view.w >= view.h) return minZoom(map, view);
  return Math.max(minZoom(map, view), view.w / worldWidth);
}

/** The part of the painting on screen, in master pixels. */
export function visibleRect(
  cam: Camera,
  view: Size,
): { x: number; y: number; w: number; h: number } {
  return { x: -cam.x / cam.zoom, y: -cam.y / cam.zoom, w: view.w / cam.zoom, h: view.h / cam.zoom };
}

/** The share (0 to 1) of a box of the painting (master pixels: x, y, w, h) that is on screen at this camera. */
export function shareOnScreen(
  box: { x: number; y: number; w: number; h: number },
  cam: Camera,
  view: Size,
): number {
  if (box.w <= 0 || box.h <= 0) return 0;
  const x0 = Math.max(0, cam.x + box.x * cam.zoom);
  const y0 = Math.max(0, cam.y + box.y * cam.zoom);
  const x1 = Math.min(view.w, cam.x + (box.x + box.w) * cam.zoom);
  const y1 = Math.min(view.h, cam.y + (box.y + box.h) * cam.zoom);
  if (x1 <= x0 || y1 <= y0) return 0;
  return ((x1 - x0) * (y1 - y0)) / (box.w * cam.zoom * box.h * cam.zoom);
}
